<template>
  <div v-if="isDialogVisible" class="reminder-dialog bring-to-frontable">
    <!-- Date Picker (above tabs) -->
    <div class="date-picker">
      <input type="date" v-model="startDate" />
      <button @click="fetchReminders" class="search-button">
        Find on/after date
      </button>
    </div>

    <!-- Tabs for My Reminders and All Reminders -->
    <div class="reminder-header">
      <div class="tabs">
        <div
          :class="{ active: activeTab === 'myReminders' }"
          @click="activeTab = 'myReminders'"
        >
          My Reminders
        </div>
        <div
          :class="{ active: activeTab === 'allReminders' }"
          @click="activeTab = 'allReminders'"
        >
          All Reminders
        </div>
      </div>
    </div>

    <!-- Search and Action Buttons (below tabs) -->
    <div class="search-actions">
      <input
        type="text"
        ref="searchQuery"
        v-model="searchTerm"
        placeholder="Search reminders"
        class="search-input"
      />
      <button @click="clearSearch" class="clear-button">Clear</button>
      <button class="new-button" @click="openReminderDialog(null)">
        New Reminder
      </button>
    </div>

    <!-- Scrollable Div for Reminder Cards -->
    <div class="reminder-list">
      <div class="action-buttons">
        <button
          @click="restoreReminder(selectedReminder)"
          class="restore-button"
        >
          Restore
        </button>
        <button @click="deleteReminder(selectedReminder)" class="delete-button">
          Delete
        </button>
        <button
          @click="hideDoneDocs"
          :title="'hide or show done\nuse [h] to toggle'"
          class=""
          :class="{ 'active-border': hideDone }"
        >
          <i class="fas fa-eye-slash"></i>
        </button>
        <button
          title="show key help"
          @click="searchHelp = !searchHelp"
          class=""
        >
          <i class="fas fa-circle-question"></i>
        </button>
        <span style="font-style: italic; color: #888"
          ><small> &larr; see help: use d, f and s keys</small></span
        >
      </div>
      <!-- Header row for table -->
      <div class="reminder-header">
        <div class="reminder-date">Due By</div>
        <div v-if="activeTab !== 'myReminders'" class="reminder-assigned-to">
          Assigned
        </div>
        <div class="reminder-title">Title</div>
        <div class="reminder-description">Description</div>
      </div>

      <!-- Collapsible sections for Future, Today, Overdue reminders -->
      <div class="scrollable-sections">
        <!-- Future Section -->
        <div
          :class="[
            'reminder-section',
            futureReminders.length ? 'has-content' : '',
          ]"
        >
          <div class="collapsible-label" @click="toggleSection('future')">
            Future
            <i
              v-if="collapsedSections.future"
              class="fas fa-sort-down collapse-icon"
            ></i>
            <i
              v-if="futureReminders.length >= 5 && !collapsedSections.future"
              class="fas fa-caret-up section-scroll-arrow"
              title="Scroll up"
            ></i>
          </div>
          <div
            v-show="!collapsedSections.future && futureReminders.length"
            class="reminder-section-list"
            ref="futureSectionList"
          >
            <div
              v-for="(reminder, index) in futureReminders"
              :key="reminder.id"
              :title="reminder.note"
              :class="[
                'reminder-item',
                {
                  'odd-row': index % 2 === 1,
                  'even-row': index % 2 === 0,
                  'selected-item': reminder.id === selectedReminder?.id,
                },
              ]"
              @dblclick="openReminderDialog(reminder)"
              @click="selectReminder(reminder)"
            >
              <div class="reminder-date" :class="getDueByClass(reminder)">
                {{ formatDate(reminder.dueBy) }}
              </div>
              <div
                v-if="activeTab !== 'myReminders'"
                class="reminder-assigned-to"
              >
                {{ reminder.assignedToDisplayName }}
              </div>
              <div class="reminder-title">{{ reminder.title }}</div>
              <div class="reminder-description">
                <span v-html="markedDescription(reminder.description)"></span>
              </div>
            </div>
          </div>
        </div>

        <!-- Today Section -->
        <div
          :class="[
            'reminder-section',
            todayReminders.length ? 'has-content' : '',
          ]"
        >
          <div class="collapsible-label" @click="toggleSection('today')">
            Due Today
            <i
              v-if="collapsedSections.today"
              class="fas fa-sort-down collapse-icon"
            ></i>
            <i
              v-if="todayReminders.length >= 5 && !collapsedSections.today"
              class="fas fa-arrow-up section-scroll-arrow"
              title="Scroll up"
            ></i>
          </div>
          <div
            v-show="!collapsedSections.today && todayReminders.length"
            class="reminder-section-list"
          >
            <div
              v-for="(reminder, index) in todayReminders"
              :title="reminder.note"
              :key="reminder.id"
              :class="[
                'reminder-item',
                {
                  'odd-row': index % 2 === 1,
                  'even-row': index % 2 === 0,
                  'selected-item': reminder.id === selectedReminder?.id,
                },
              ]"
              @dblclick="openReminderDialog(reminder)"
              @click="selectReminder(reminder)"
            >
              <div class="reminder-date" :class="getDueByClass(reminder)">
                {{ formatDate(reminder.dueBy) }}
              </div>
              <div
                v-if="activeTab !== 'myReminders'"
                class="reminder-assigned-to"
              >
                {{ reminder.assignedToDisplayName }}
              </div>
              <div class="reminder-title">{{ reminder.title }}</div>
              <div class="reminder-description">{{ reminder.description }}</div>
            </div>
          </div>
        </div>

        <!-- Overdue Section -->
        <div
          :class="[
            'reminder-section',
            overdueReminders.length ? 'has-content' : '',
          ]"
        >
          <div class="collapsible-label" @click="toggleSection('overdue')">
            Overdue
            <i
              v-if="collapsedSections.overdue"
              class="fas fa-sort-down collapse-icon"
            ></i>
            <i
              v-if="overdueReminders.length >= 5 && !collapsedSections.overdue"
              class="fas fa-arrow-up section-scroll-arrow"
              title="Scroll up"
            ></i>
          </div>
          <div
            v-show="!collapsedSections.overdue && overdueReminders.length"
            class="reminder-section-list"
          >
            <div
              v-for="(reminder, index) in overdueReminders"
              :key="reminder.id"
              :title="reminder.note"
              :class="[
                'reminder-item',
                {
                  'odd-row': index % 2 === 1,
                  'even-row': index % 2 === 0,
                  'selected-item': reminder.id === selectedReminder?.id,
                },
              ]"
              @dblclick="openReminderDialog(reminder)"
              @click="selectReminder(reminder)"
            >
              <div class="reminder-date" :class="getDueByClass(reminder)">
                {{ formatDate(reminder.dueBy) }}
              </div>
              <div
                v-if="activeTab !== 'myReminders'"
                class="reminder-assigned-to"
              >
                {{ reminder.assignedToDisplayName }}
              </div>
              <div class="reminder-title">{{ reminder.title }}</div>
              <div class="reminder-description">{{ reminder.description }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="note-label">Note</div>
      <div class="reminder-note-area" v-html="renderedReminderNote"></div>
    </div>

    <!-- New/Edit Reminder Modal Popup -->
    <div
      v-if="showEditDialog"
      class="draggable-popup reminder-popup bring-to-frontable"
    >
      <div class="popup-content">
        <div class="reminder-popup-title" @click="bringToFront($event)">
          {{ currentReminder ? 'Edit Reminder' : 'New Reminder' }}
          <!-- close -->
          <i
            class="fas fa-close"
            style="position: absolute; right: 36px; top: 10px"
            @click.stop="showEditDialog = false"
          ></i>
          <!-- Draggable Icon -->
          <i
            class="fas fa-crosshairs draggable-icon"
            :class="{ active: isDragMode }"
            ref="ledgerListPopupIcon"
            @click.stop="toggleDragMode"
          ></i>
        </div>

        <div class="form-row">
          <label>Title:</label>
          <input
            ref="editedTitle"
            type="text"
            v-model="editedReminder.title"
            class="form-input"
          />
        </div>

        <div class="form-row">
          <label>Description (Markdown supported):</label>
          <textarea
            v-model="editedReminder.description"
            class="form-textarea"
          ></textarea>
          <div
            class="markdown-preview"
            v-html="renderMarkdown(editedReminder.description)"
          ></div>
        </div>

        <div class="form-row">
          <label>Note (Markdown supported):</label>
          <textarea
            v-model="editedReminder.note"
            class="form-textarea"
          ></textarea>
          <div
            class="markdown-preview"
            v-html="renderMarkdown(editedReminder.note)"
          ></div>
        </div>

        <div class="form-columns">
          <div class="column">
            <label>Due By:</label>
            <input type="date" v-model="formattedDueBy" class="form-input" />

            <label>Remind On:</label>
            <input type="date" v-model="formattedRemindOn" class="form-input" />

            <label>Priority:</label>
            <select v-model="editedReminder.priority" class="form-select">
              <option value="1">Low</option>
              <option value="2">Medium</option>
              <option value="3">High</option>
            </select>
          </div>

          <div class="column">
            <label>Category:</label>
            <select
              v-model="editedReminder.category"
              class="form-select category-select"
              @change="handleCategoryChange"
            >
              <option
                v-for="category in categories"
                :value="category.id"
                :key="category.id"
              >
                {{ category.categoryName }}
              </option>
              <option value="new">New...</option>
            </select>
            <div v-if="showNewCategoryDialog" class="new-category-dialog">
              <input
                v-model="newCategoryName"
                placeholder="New category name"
                @keyup.enter="addNewCategory"
              />
              <button @click="addNewCategory">Add</button>
              <button @click="showNewCategoryDialog = false">Cancel</button>
            </div>

            <label>Assigned To:</label>
            <select v-model="editedReminder.assignedTo" class="form-select">
              <option v-for="user in users" :value="user.id" :key="user.id">
                {{ user.displayName }}
              </option>
            </select>

            <label>Completed By:</label>
            <select v-model="editedReminder.completedBy" class="form-select">
              <option v-for="user in users" :value="user.id" :key="user.id">
                {{ user.displayName }}
              </option>
            </select>
          </div>
        </div>

        <div class="popup-actions">
          <button @click="saveReminder" class="save-button">Save</button>
          <button @click="closeReminderDialog" class="cancel-button">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- New Category Dialog -->
    <div
      v-if="showNewCategoryDialog"
      class="draggable-popup reminder-popup bring-to-frontable"
      style="width: 400px"
    >
      <div class="popup-content">
        <div class="reminder-popup-title" @click="bringToFront($event)">
          Add New Category
          <!-- close -->
          <i
            class="fas fa-close"
            style="position: absolute; right: 36px; top: 10px"
            @click.stop="showNewCategoryDialog = false"
          ></i>
        </div>

        <div class="form-row">
          <label>Category Name:</label>
          <input
            type="text"
            v-model="newCategoryName"
            class="form-input"
            placeholder="Enter category name"
          />
        </div>

        <div class="popup-actions">
          <button @click="addNewCategory" class="save-button">
            Add Category
          </button>
          <button @click="showNewCategoryDialog = false" class="cancel-button">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="searchHelp" class="help-overlay">
    <div class="search-help">
      <div class="tips">
        Tips for use
        <button class="help-close" @click="searchHelp = false">
          <i class="fas fa-close"></i>
        </button>
      </div>
      <div style="font-size: 0.8em; margin-bottom: 5px; margin-left: 12px">
        Searching: type a pattern in the search box. You can combine multiple
        layers of searching using commas. The Help menu bar above has other
        shortcuts you can use. You can also use keypresses defined below in the
        table.
      </div>
      <div class="table-wrapper">
        <table style="margin-bottom: 35px">
          <thead>
            <tr>
              <td style="width: 135px">Pattern or [keypress]</td>
              <td>search Result</td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>anything, other</td>
              <td>
                first search for 'anything', then refine the search by looking
                for 'other' within those results
              </td>
            </tr>
            <tr>
              <td style="cursor: pointer">alt-r</td>
              <td>shows or hides the reminders popup</td>
            </tr>
            <tr>
              <td style="cursor: pointer">alt-x</td>
              <td>shows or hides the navigation popup</td>
            </tr>
            <tr>
              <td style="cursor: pointer">[d]</td>
              <td>
                select a row in the table; pressing 'd' marks that document as
                'done' in the reminders (or un-marks it as done, if its already
                marked done.)
              </td>
            </tr>
            <tr>
              <td style="cursor: pointer">[f]</td>
              <td>
                select a row in the table; pressing 'f' finds that document in
                the document manager and closes the reminder dialog.
              </td>
            </tr>
            <tr>
              <td style="cursor: pointer">[s]</td>
              <td>
                select a row in the table; pressing 's' searches for more
                reminders using a few words from the current one.
              </td>
            </tr>
            <tr>
              <td style="cursor: pointer">[ESC]</td>
              <td>
                pressing Escape clears the search input and shows all documents
                again.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import debounce from 'lodash/debounce';
import eventBus from './eventBus.js'; // Path to eventBus.js
import { marked } from 'marked'; // Markdown parser

export default {
  props: ['showReminderDialog', 'linkedDoc', 'linkedProperty'],
  data() {
    const today = new Date();
    const lookBack = new Date(today);
    lookBack.setDate(today.getDate() - 365);
    const formattedDate = this.formatDateForInput(lookBack);
    return {
      searchHelp: false,
      hideDone: true,
      selectedReminder: null,
      isSearchFocused: false,
      isDialogVisible: true, // Track dialog visibility
      activeTab: 'allReminders',
      reminders: [],
      users: [],
      categories: [],
      searchTerm: '',
      startDate: formattedDate,
      showEditDialog: false,
      currentReminder: null,
      editedReminder: {
        id: null,
        title: '',
        description: '',
        note: '',
        dueBy: '',
        remindOn: '',
        priority: '1',
        assignedTo: null,
        completedBy: null,
        category: null,
        linkedDoc: this.linkedDoc,
        linkedProperty: this.linkedProperty,
      },
      collapsedSections: {
        future: false,
        today: false,
        overdue: false,
      },
      isDragMode: false,
      popupPosition: { x: 300, y: 300 }, // Initial popup position
      currentPopup: null, // Holds the currently targeted popup reference
      currentIconOffset: { x: 0, y: 0 }, // Holds the icon offset for dynamic calculations
      showNewCategoryDialog: false,
      newCategoryName: '',
    };
  },

  watch: {
    showReminderDialog(newVal) {
      console.log('showReminderDialog', newVal);
      this.isDialogVisible = newVal;
    },

    showEditDialog(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (this.$refs.editedTitle) {
            this.$refs.editedTitle.focus();
          }
        });
      }
    },

    startDate() {
      this.fetchReminders();
    },
    selectedReminder: {
      handler: debounce(function () {
        this.currentReminder = this.selectedReminder;
        // this.saveReminder(); // Call the saveReminders function after the debounce
      }, 1000), // 1000ms debounce delay
      deep: true, // Enable deep watching for nested objects
    },
    reminders: {
      handler() {
        this.$nextTick(() => {
          if (
            this.$refs.futureSectionList &&
            this.$refs.futureSectionList.scrollHeight
          ) {
            this.$refs.futureSectionList.scrollTop =
              this.$refs.futureSectionList.scrollHeight;
          }
        });
      },
      deep: true,
    },
  },

  computed: {
    renderedReminderNote() {
      return marked.parse(this.selectedReminder?.note || '');
    },

    formattedDueBy: {
      get() {
        return this.formatSimpleDate(this.editedReminder.dueBy);
      },
      set(value) {
        this.editedReminder.dueBy = value;
      },
    },

    formattedRemindOn: {
      get() {
        return this.formatSimpleDate(this.editedReminder.remindOn);
      },
      set(value) {
        this.editedReminder.remindOn = value;
      },
    },
    filteredReminders() {
      const term = this.searchTerm.toLowerCase();
      const tokens = term.split(',').map((token) => token.trim().toLowerCase());

      // Filter out reminders where 'dueBy' is null or undefined
      let filteredResults = this.reminders.filter((reminder) => reminder.dueBy);

      // If there is a search term, filter based on the term
      if (this.searchTerm) {
        tokens.forEach((token) => {
          filteredResults = filteredResults.filter((reminder) => {
            return [
              reminder.title,
              reminder.description,
              reminder.note,
              this.formatDate(reminder.dueBy),
              reminder.assignedToDisplayName,
              reminder.completedByDisplayName,
              reminder.categoryName,
            ]
              .filter(Boolean) // Filter out null/undefined fields
              .some((field) => field.toLowerCase().includes(token));
          });
        });
      }

      // Filter by My Reminders if the activeTab is 'myReminders'
      if (this.activeTab === 'myReminders') {
        const currentUser = this.users
          ? this.users.find((user) => user.you === 1)
          : null; // Find the user where 'you' === 1

        if (currentUser) {
          filteredResults = filteredResults.filter((reminder) => {
            return reminder.assignedTo === currentUser.id; // Match reminders where assignedTo matches the 'you' user id
          });
        } else {
          console.warn('No user found with you === 1 or users list is empty');
        }
      }

      // optionally filter out done tasks
      filteredResults = filteredResults.filter((reminder) => {
        return !(this.hideDone && reminder.completedOn);
      });

      return filteredResults;
    },

    futureReminders() {
      return this.filteredReminders.filter((reminder) => {
        const dueDate = new Date(reminder.dueBy);
        // Only compare the date portion
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set today's time to 00:00:00
        dueDate.setHours(0, 0, 0, 0); // Set the reminder's due date time to 00:00:00
        return dueDate > today;
      });
    },

    todayReminders() {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set today's time to 00:00:00
      const todayTimestamp = today.getTime(); // Get today's date timestamp

      return this.filteredReminders.filter((reminder) => {
        const dueDate = new Date(reminder.dueBy);
        const dueDateTimestamp = dueDate.setHours(0, 0, 0, 0); // Normalize the reminder's due date time and get the timestamp

        return dueDateTimestamp === todayTimestamp;
      });
    },

    overdueReminders() {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set today's time to 00:00:00
      const todayTimestamp = today.getTime(); // Get the timestamp for comparison

      return this.filteredReminders.filter((reminder) => {
        const dueDate = new Date(reminder.dueBy);
        const dueDateTimestamp = dueDate.setHours(0, 0, 0, 0); // Set the reminder's due date time to 00:00:00 and get the timestamp

        return dueDateTimestamp < todayTimestamp;
      });
    },
  },
  methods: {
    formatSimpleDate(dateString) {
      if (!(dateString && typeof dateString == 'string')) return '';
      return dateString.slice(0, 10); // Ensure it matches YYYY-MM-DD
    },

    hideDoneDocs() {
      this.hideDone = !this.hideDone;
    },

    selectReminder(reminder) {
      this.selectedReminder = reminder;
    },

    fetchReminders() {
      const params = {
        startDate: this.startDate,
      };
      axios
        .get('/api/reminders', { params })
        .then((response) => {
          this.reminders = response.data;
        })
        .catch((error) => {
          console.error('Error fetching reminders:', error);
        });
    },
    highlightSearch(text) {
      if (!this.searchTerm) return text;
      const regex = new RegExp('(' + this.searchTerm + ')', 'gi');
      return text.replace(regex, '<mark>$1</mark>');
    },
    renderMarkdown(text) {
      return marked.parse(text || ''); // Parse the text using marked
    },

    clearSearch() {
      this.searchTerm = '';
    },

    openReminderDialog(reminder = null) {
      this.currentReminder = reminder;
      if (reminder) {
        this.editedReminder = { ...reminder };
      } else {
        this.editedReminder = {
          id: null,
          title: '',
          description: '',
          note: '',
          dueBy: new Date(),
          remindOn: new Date(),
          priority: '1',
          assignedTo: null,
          completedBy: null,
          category: null,
          linkedDoc: this.linkedDoc,
          linkedProperty: this.linkedProperty,
        };
      }
      this.showEditDialog = true;
    },

    closeReminderDialog() {
      this.showEditDialog = false;
    },

    closeDialog() {
      this.isDialogVisible = false;
      console.log('Emitting update:showReminderDialog from ReminderDialog');
      eventBus.emit('update:showReminderDialog', false);
    },

    saveReminder() {
      const apiUrl = '/api/document-reminders';
      const method = 'post';

      axios[method](apiUrl, this.editedReminder)
        .then(() => {
          this.showEditDialog = false;
          this.fetchReminders();
        })
        .catch((error) => {
          console.error('Error savin/closeg reminder:', error);
        });
    },

    deleteReminder(reminder) {
      axios
        .delete(`/api/reminders/${reminder.id}`)
        .then(() => {
          this.fetchReminders();
        })
        .catch((error) => {
          console.error('Error deleting reminder:', error);
        });
    },

    restoreReminder(reminder) {
      axios
        .put(`/api/reminders/${reminder.id}/restore`)
        .then(() => {
          this.fetchReminders();
        })
        .catch((error) => {
          console.error('Error restoring reminder:', error);
        });
    },
    toggleSection(section) {
      this.collapsedSections[section] = !this.collapsedSections[section];
    },

    formatDate(dateInput) {
      // If the input is null/undefined, return early
      if (!dateInput) return;

      let date;

      // If it's a string in ISO 8601 format, parse it as a Date object
      if (typeof dateInput === 'string' && dateInput.includes('T')) {
        date = new Date(dateInput); // ISO format
      }
      // If it's a string in YYYY-MM-DD format, manually parse it to avoid timezone issues
      else if (typeof dateInput === 'string' && dateInput.includes('-')) {
        const [year, month, day] = dateInput.split('-');
        date = new Date(year, month - 1, day); // Create a new Date without the timezone shift
      }
      // If it's already a Date object, use it as is
      else {
        date = dateInput;
      }

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      // Extract the local date components without any timezone adjustments
      const year = String(date.getFullYear()).slice(-2); // Get the last two digits of the year
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Get the month and pad with 0
      const day = String(date.getDate()).padStart(2, '0'); // Get the day and pad with 0

      // Return the formatted date as MM/DD/YYYY
      const formatted = `${month}/${day}/${year}`;

      return formatted;
    },

    async fetchUsers() {
      try {
        const response = await axios.get('/api/users');
        this.users = response.data || [];
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    },
    getDueByClass(item) {
      return item.dueBy && item.completedOn ? 'strikethrough' : '';
    },
    formatDateForInput(date) {
      // Format the date as yyyy-MM-dd
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    handleKeyPress(event) {
      if (this.showEditDialog) return;
      // Check if the 'f' key is pressed and selectedReminder is not null
      if (event.key === 'Escape') {
        this.searchTerm = '';
        this.$refs.searchQuery.focus();
      }
      if (this.selectedReminder && event.key === 's') {
        this.searchTerm =
          this.selectedReminder.title.split(' ')[0] +
          ',' +
          this.selectedReminder.title.split(' ')[1];
      }
      if (this.selectedReminder && event.key === 'd') {
        // Check if there is a dueBy date
        // const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format
        const today = new Date().toISOString().split('T')[0];
        if (!this.selectedReminder.dueBy) {
          this.selectedReminder.dueBy = today; // Set dueBy to today
        }
        if (this.selectedReminder.completedOn) {
          // If completedOn is already set, toggle it to null (remove the date)
          this.selectedReminder.completedOn = null;
        } else {
          // If completedOn is null, set it to today's date
          this.selectedReminder.completedOn = today; // Set completedOn to today
        }
        this.editedReminder = this.selectedReminder;
        this.saveReminder();
        return;
      }

      if (this.selectedReminder && event.key === 'f') {
        // Emit the selectedReminder.linkedDoc to the parent component
        eventBus.emit('select-document', this.selectedReminder.linkedDoc);

        // Close the reminder dialog
        this.closeDialog();
      }
    },

    bringToFront(event) {
      console.log('bring');
      // Find the parent element of the clicked title bar
      const targetElement = event.target.closest('.bring-to-frontable');

      if (targetElement) {
        // Get all elements with .bring-to-frontable class and calculate the highest z-index
        const allElements = Array.from(
          document.querySelectorAll('.bring-to-frontable'),
        );
        const maxZIndex = allElements.reduce((max, el) => {
          const zIndex = parseInt(window.getComputedStyle(el).zIndex) || 0;
          return Math.max(max, zIndex);
        }, 0);

        // Set the z-index of the clicked modal to be one higher than the current highest
        targetElement.style.zIndex = maxZIndex + 1;
      }
    },

    setPopupPosition(event) {
      // Ensure there is a popup to position
      if (this.currentPopup) {
        // Move the popup to align the icon with the new click location
        this.popupPosition.x = event.clientX - this.currentIconOffset.x;
        this.popupPosition.y = event.clientY - this.currentIconOffset.y;

        // Apply inline styles dynamically to the popup's position
        this.currentPopup.style.left = `${this.popupPosition.x}px`;
        this.currentPopup.style.top = `${this.popupPosition.y}px`;

        // Reset drag mode and remove event listener
        this.isDragMode = false;
        document.removeEventListener('click', this.setPopupPosition);
      }
    },
    toggleDragMode(event) {
      // Set drag mode to true and identify the targeted icon and popup container
      this.isDragMode = !this.isDragMode;

      if (this.isDragMode) {
        // Capture the icon clicked and determine its closest popup container
        const iconElement = event.target;
        const popupContainer = iconElement.closest('.draggable-popup'); // Generalize by class

        if (popupContainer) {
          this.currentPopup = popupContainer;

          // Get bounding rectangles to calculate the offset
          const iconRect = iconElement.getBoundingClientRect();
          const popupRect = popupContainer.getBoundingClientRect();

          // Calculate offset between the icon and the popup's top-left corner
          this.currentIconOffset.x = iconRect.left - popupRect.left;
          this.currentIconOffset.y = iconRect.top - popupRect.top;

          // Add a one-time event listener to capture the next click location
          document.addEventListener('click', this.setPopupPosition);
        }
      } else {
        // If drag mode is off, remove the listener
        document.removeEventListener('click', this.setPopupPosition);
      }
    },
    markedDescription(description) {
      // Ensure description is not null or undefined before parsing
      return marked.parse(description || '');
    },
    async fetchCategories() {
      try {
        const response = await axios.get('/api/reminderCategory');
        this.categories = response.data || [];
      } catch (error) {
        console.error('Error fetching reminder categories:', error);
      }
    },
    async addNewCategory() {
      if (!this.newCategoryName.trim()) return;
      try {
        const response = await axios.post('/api/reminderCategory', {
          categoryName: this.newCategoryName,
        });
        this.categories.push(response.data);
        this.editedReminder.category = response.data.id;
        this.showNewCategoryDialog = false;
        this.newCategoryName = '';
      } catch (error) {
        alert('Failed to add category');
      }
    },
    handleCategoryChange(e) {
      if (e && e.target && e.target.value === 'new') {
        this.showNewCategoryDialog = true;
        this.newCategoryName = '';
      }
    },
  },
  created() {
    this.fetchReminders();
    this.fetchUsers();
  },

  mounted() {
    window.addEventListener('keydown', this.handleKeyPress);
    this.fetchCategories();
    // Set initial scroll position for Future section to bottom
    this.$nextTick(() => {
      // Use a Vue ref for the future section list
      if (
        this.$refs.futureSectionList &&
        this.$refs.futureSectionList.scrollHeight
      ) {
        this.$refs.futureSectionList.scrollTop =
          this.$refs.futureSectionList.scrollHeight;
      }
    });
  },
  beforeUnmount() {
    // Remove the event listener when the component is destroyed
    window.removeEventListener('keydown', this.handleKeyPress);
  },
};
</script>

<style scoped>
/* Styles reused from NotesPanel.vue */
.reminder-dialog {
  padding: 20px;
  background-color: #f9f9f9;
  position: relative;
  min-height: 400px;
}

.close-button {
  position: absolute;
  top: 20px;
  right: 20px;
  background: #dddddd;
  border: none;
  font-size: 18px;
  cursor: pointer;
  border: 1pt solid #ccc;
  border-radius: 4px;
}

.date-picker {
  margin-bottom: 10px;
}

.reminder-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.tabs {
  display: flex;
}

.tabs div {
  margin-right: 10px;
  padding: 10px;
  cursor: pointer;
}

.tabs div.active {
  font-weight: bold;
  border-bottom: 2px solid #000;
}

.search-actions {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.search-input {
  padding: 5px;
  flex: 1;
  margin-right: 10px;
}

.clear-button,
.new-button,
.search-button {
  padding: 5px 10px;
  margin-left: 4px;
  border-radius: 4px;
  background-color: #a080a8;
  color: white;
  border: none;
  cursor: pointer;
  margin-right: 5px;
}

.reminder-list {
  margin-top: 10px;
  cursor: pointer;
  user-select: none;
}

.scrollable {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 10px;
}

.markdown-preview {
  margin-top: 10px;
  padding: 10px;
  background-color: #fefefe;
  border: 1pt solid #eee;
  border-radius: 5px;
  height: 52px;
  overflow-y: auto;
  font-size: 0.8em;
}

.reminder-popup {
  position: fixed;
  top: 163px;
  left: 123px;
  background-color: white;
  box-shadow: 6px 6px 10px 6px rgba(0, 0, 0, 0.3);
  border-radius: 5px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
  border-bottom-left-radius: 5px;
  border: 1px solid #666;
  z-index: 1000;
  width: 500px;
}

.reminder-popup textarea {
  width: 96%;
}

.reminder-popup input,
.reminder-popup select {
  padding: 5px;
  border-radius: 4px;
  border: 1pt solid #ddd;
}

.popup-content {
  display: flex;
  flex-direction: column;
  width: 92%;
  padding-left: 10px;
}

.popup-actions {
  margin-top: 10px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
}

.reminder-header {
  display: flex;
  padding: 4px;
  padding-left: 4px;
  border-bottom: 2px solid lightgrey;
  font-size: 0.9em;
  margin-top: 7px;
  background: #ddd;
}

.reminder-item {
  display: flex;
  align-items: center;
  padding: 2px;
  border-bottom: 1px solid lightgrey;
  font-size: 0.75em;
  white-space: nowrap; /* Prevent text from wrapping */
  overflow: hidden; /* Hide overflowing text */
  text-overflow: ellipsis; /* Add ellipsis to overflowing text */
}

.reminder-date,
.reminder-title,
.reminder-description {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.reminder-description :deep(p) {
  margin-top: 0;
  margin-bottom: 0;
}

.reminder-date {
  overflow: hidden;
  text-overflow: ellipsis; /* Add ellipsis to overflowing text */
  width: 65px;
}

.reminder-title {
  overflow: hidden;
  text-overflow: ellipsis; /* Add ellipsis to overflowing text */
  width: 230px;
}
.reminder-assigned-to {
  overflow: hidden;
  text-overflow: ellipsis; /* Add ellipsis to overflowing text */
  width: 120px;
}

.reminder-description {
  overflow: hidden;
  text-overflow: ellipsis; /* Add ellipsis to overflowing text */
  flex-grow: 1;
  width: 230px;
}

.odd-row {
  background-color: #e0e0e0;
}

.even-row {
  background-color: #ffffff;
}

.collapsible-label {
  font-weight: bold;
  font-size: 0.9em;
  padding: 5px;
  cursor: pointer;
  border-bottom: 1px solid #888;
  background-color: #f2d6f9;
  position: relative;
}

.section-scroll-arrow {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #7a5fa0;
  font-size: 1.1em;
  cursor: pointer;
  pointer-events: none; /* purely visual, not clickable */
}

.collapse-icon {
  margin-left: 10px;
}
.selected-item {
  background-color: #907793 !important;
}
.active-border {
  border: 1pt solid #765e7c;
  border-radius: 2px;
  background-color: #a080a8;
}
/* Strikethrough effect for dueBy column */
.strikethrough {
  text-decoration: line-through;
}
.action-buttons button {
  margin-left: 5px;
}

.search-help {
  user-select: none;
  cursor: pointer;
  position: absolute;
  left: 150px;
  top: 150px;
  z-index: 6000;
  width: 650px;
  box-shadow: 9px 13px 7px 4px rgb(0 0 0 / 22%);
  padding: 10px;
  background: #d5d5d5;
  border-radius: 4px;
  border: 1pt solid #555;
  font-family: Arial, sans-serif;
}

.search-help .table-wrapper {
  overflow-y: auto;
}

.search-help table {
  width: 97%;
  border-collapse: collapse;
  font-size: 0.8em;
  font-family: consolas;
  margin-left: 10px;
}

.search-help th,
.search-help td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #ccc;
}

.search-help thead td {
  font-weight: bold;
  background-color: #4b0082;
  color: white;
}

.search-help tbody tr:nth-child(even) {
  background-color: #f0f0f0;
}

.search-help tbody tr:hover {
  background-color: #ddd;
}

.search-help td {
  word-wrap: break-word;
  max-width: 150px;
}

.search-help th {
  background-color: #444;
  color: white;
  font-weight: bold;
}

.search-help table td:first-child {
  font-weight: bold;
}

.search-help td {
  padding: 6px;
  border-bottom: 1px solid #ddd;
}

.search-help .tips {
  margin-bottom: 4px;
  margin-top: -10px;
  margin-left: -10px;
  margin-right: -10px;
  padding: 8px;
  background: #333;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  color: #fff;
  font-size: 1.6em;
  text-align: center;
}
/* Style for the close button */
.search-help .close-button {
  position: absolute;
  bottom: 10px;
  right: 18px;
}

/* Overlay for the semi-transparent background */
.help-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
  z-index: 5000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.help-close {
  position: absolute;
  right: 17px;
  top: 11px;
}

.draggable-icon {
  cursor: pointer;
  color: #888;
  transition: color 0.3s;
  position: absolute;
  right: 10px;
}

.draggable-icon.active {
  color: #007bff;
}

.reminder-popup-title {
  background: #333;
  color: #eee;
  padding: 10px;
  /* margin-top: -20px; */
  margin-left: -11px;
  margin-right: -30px;
  margin-bottom: 15px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  text-align: center;
}

.reminder-popup-title i {
  color: #888;
}

.popup-content label {
  margin-top: 5px;
  font-size: 0.9em;
  color: #000;
}

.form-row {
  margin-bottom: 1rem;
}

.form-columns {
  display: flex;
  gap: 20px; /* Space between columns */
}

.column {
  flex: 1; /* Each column takes up equal width */
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-input,
.form-select {
  width: 100%;
}

.popup-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

/* Add to <style scoped> */
.scrollable-sections {
  display: flex;
  flex-direction: column;
  max-height: 350px;
  gap: 6px;
  height: 400px;
}
.reminder-section {
  display: flex;
  flex-direction: column;
  background: #f8f8f8;
  border-radius: 7px;
  border: 1px solid #e0e0e0;
  margin-bottom: 0;
  overflow: hidden;
  min-height: 25px;
  /* Only grow if there is content */
  flex: unset;
}
.reminder-section.has-content {
  flex: 1 1 0;
}
.reminder-section-list {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 2px;
  /* Hide scrollbars for all browsers */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
}
.reminder-section-list::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}
.collapsible-label {
  font-weight: bold;
  background: #d3d3d3;
  padding: 0px 5px;
  cursor: pointer;
  border-bottom: 1px solid #e0e0e0;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  min-height: 25px;
  display: flex;
  align-items: center;
}

.note-label {
  font-size: 0.8em;
  margin-top: 8px;
}
.reminder-note-area {
  font-size: 0.75em;
  height: 80px;
  overflow-y: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
}
.reminder-note-area::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

/* New styles for category dialog */
.new-category-dialog {
  display: flex;
  gap: 6px;
  margin-top: 6px;
  align-items: center;
}
.new-category-dialog input {
  flex: 1;
  padding: 3px 6px;
  font-size: 0.95em;
}
.new-category-dialog button {
  padding: 3px 10px;
  font-size: 0.95em;
  border-radius: 3px;
  border: 1px solid #bbb;
  background: #eaeaea;
  cursor: pointer;
}

.category-select {
  max-height: 80px;
  overflow-y: auto;
  /* Hide scrollbars for all browsers */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
}
.category-select::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}
</style>
